import { useRef } from 'react';
import PropTypes from 'prop-types';

import { Btn } from './SingleFileUploaderIsolated.style';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const SUPPORTED_MIME_TYPES_REGEX = /image/i;
const MIME_TYPES_NOT_TO_BE_PROCESSED = ['application/pdf'];
const MIME_TYPES_TO_BE_EXCLUDED_FROM_COMPRESSION = ['application/pdf'];
const MAX_IMG_WIDTH = 800;
const MAX_IMG_HEIGHT = 800;
const IMG_QUALITY = 0.7;
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const SingleFileUploaderIsolated = ({
  children,
  supportedMimeTypesRegex,
  fileInputAcceptAttrRegex,
  indexToPrepend,
  fileData,
  setUploadedData,
}) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const supportedMimeTypes = supportedMimeTypesRegex || SUPPORTED_MIME_TYPES_REGEX;
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const fileInput = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  const createWindowUrlFromFile = file => {
    window.URL = window.URL || window.webkitURL;
    const fileUrl = window.URL.createObjectURL(file.fileData);
    return fileUrl;
  };

  const createImageFromFile = fileUrl =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.src = fileUrl;

      image.onload = () => {
        resolve(image);
      };

      image.onerror = reject;
    });

  const resizeAndCompressImage = async image => {
    const { width, height } = image;
    const compressionDimensions = {};
    let dimenParamToChange = '';
    let ratio = 0;

    if (width > height) {
      const maxWidth = Math.min(width, MAX_IMG_WIDTH);
      compressionDimensions.width = maxWidth;
      compressionDimensions.height = Math.round((height * maxWidth) / width);
      dimenParamToChange = 'height';
      ratio = Math.round((height / width) * 10) / 10;
    } else {
      const maxHeight = Math.min(height, MAX_IMG_HEIGHT);
      compressionDimensions.width = Math.round((width * maxHeight) / height);
      compressionDimensions.height = maxHeight;
      dimenParamToChange = 'width';
      ratio = Math.round((width / height) * 10) / 10;
    }

    const dimensions = {
      dimenParamToChange,
      ratio,
    };

    const canvas = document.createElement('canvas');
    canvas.width = compressionDimensions.width;
    canvas.height = compressionDimensions.height;
    const canvasCtx = canvas.getContext('2d');
    canvasCtx.drawImage(image, 0, 0, compressionDimensions.width, compressionDimensions.height);

    return {
      compressedImageCanvas: canvas,
      dimensions,
    };
  };

  const createFileFromCanvas = (canvas, fileName, fileType) => {
    const imageDataUrl = canvas.toDataURL(fileType, IMG_QUALITY);
    const imageDataUrlArr = imageDataUrl.split(',');
    const imageMimeType = imageDataUrlArr[0].match(/:(.*?);/)[1];
    const imageBase64 = imageDataUrlArr[1];
    const imageDecodedBase64 = atob(imageBase64);
    let imageDecodedBase64Length = imageDecodedBase64.length;
    const imageBinaryDataArr = new Uint8Array(imageDecodedBase64Length);
    // eslint-disable-next-line no-plusplus
    while (imageDecodedBase64Length--) {
      imageBinaryDataArr[imageDecodedBase64Length] =
        imageDecodedBase64.charCodeAt(imageDecodedBase64Length);
    }
    const compressedImageFile = new File([imageBinaryDataArr], fileName, { type: imageMimeType });

    return {
      compressedImageFile,
      imageDataUrl,
    };
  };

  const processFile = async file => {
    window.URL = window.URL || window.webkitURL;
    const fileUrl = window.URL.createObjectURL(file.fileData);

    const image = await createImageFromFile(fileUrl);

    let compressedImageCanvas = image;
    let dimensions = {};
    if (file.shouldBypassCompression === false)
      ({ compressedImageCanvas, dimensions } = await resizeAndCompressImage(image));

    const { compressedImageFile, imageDataUrl } = createFileFromCanvas(
      compressedImageCanvas,
      file.fileName,
      file.fileType,
    );

    const processedFileObj = {
      ...file,
      dataUrl: imageDataUrl,
      isProcessed: true,
      compressedFileData: compressedImageFile,
      dimensions,
    };

    return processedFileObj;
  };

  const prepareUploadedFileForProcessing = async uploadedFile => {
    let uploadedFileData = { ...uploadedFile };

    if (uploadedFileData.isProcessed === false) {
      // we should use promise.all instead
      // eslint-disable-next-line no-await-in-loop
      uploadedFileData = await processFile(uploadedFileData);
    } else {
      const fileDataUrl = createWindowUrlFromFile(uploadedFileData);
      uploadedFileData = {
        ...uploadedFileData,
        dataUrl: fileDataUrl,
      };
    }

    setUploadedData(uploadedFileData);
  };
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleFileInputChange = event => {
    const uploadedFile = event.target.files[0];

    const { name, type } = uploadedFile;

    const isFileTypeSupported = supportedMimeTypes.test(type);

    if (isFileTypeSupported && fileData?.fileName !== name) {
      const shouldBeProcessed = MIME_TYPES_NOT_TO_BE_PROCESSED.includes(type) === false;
      const shouldBypassCompression = MIME_TYPES_TO_BE_EXCLUDED_FROM_COMPRESSION.includes(type);

      const fileObj = {
        fileData: uploadedFile,
        fileName: name,
        fileType: type,
        dataUrl: '',
        shouldBeProcessed,
        isProcessed: !shouldBeProcessed,
        shouldBypassCompression,
        compressedFileData: (shouldBypassCompression && uploadedFile) || null,
        dimensions: {},
      };

      if (fileInput && fileInput.current) fileInput.current.value = '';
      // setUploadedData(fileObj);
      prepareUploadedFileForProcessing(fileObj);
    }
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################

  const fileInputAcceptAttr = fileInputAcceptAttrRegex || 'image/*';

  return (
    <>
      <Btn
        id={`file-uploader-btn-${indexToPrepend}`}
        type="file"
        accept={fileInputAcceptAttr}
        ref={fileInput}
        onChange={event => handleFileInputChange(event)}
      />
      {children}
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

SingleFileUploaderIsolated.propTypes = {
  children: PropTypes.node.isRequired,
  supportedMimeTypesRegex: PropTypes.instanceOf(RegExp).isRequired,
  fileInputAcceptAttrRegex: PropTypes.string.isRequired,
  indexToPrepend: PropTypes.number,
  fileData: PropTypes.shape({
    fileName: PropTypes.string,
  }),
  setUploadedData: PropTypes.func.isRequired,
};

SingleFileUploaderIsolated.defaultProps = {
  indexToPrepend: 0,
  fileData: null,
};

export default SingleFileUploaderIsolated;
